import type { AnyID, LinkedItem } from '@types'
import { getArmSideSign } from '@utils/linkedArms/armPlacement'
import { createArmRowContext, type RowSide } from '@utils/linkedArms/armRowContext'
import { getRelativeMatrix } from '@utils/sceneObjects/items/getRelativeMatrix'
import {
  isCornerXLBrazoCatalog,
  isModularCornerSnapCatalog,
  isOreja,
  isRinconeroCatalog,
  rinconeroBaseCatalogCode,
  rinconeroPlanFondo,
  rinconeroPlanWidth,
  alignLegSnapDepthToAnchorBackPlane,
  type RinconeroUnitLike,
} from '@utils/sceneObjects/items/rinconeroUtils'
import { Matrix4, Quaternion, Vector3 } from 'three'

type RowUnitLike = {
  id?: AnyID
  catalogID?: unknown
  width?: number
  thickness?: number
  morphs?: Array<{ name?: string }>
  matrix?: Matrix4 | { decompose: (p: Vector3, q: Quaternion, s: Vector3) => void }
}

const toMatrix4 = (matrix: RowUnitLike['matrix']): Matrix4 | null => {
  if (!matrix) return null
  if (matrix instanceof Matrix4) return matrix
  const pos = new Vector3()
  const quat = new Quaternion()
  const scale = new Vector3()
  matrix.decompose(pos, quat, scale)
  return new Matrix4().compose(pos, quat, scale)
}

const getWorldPos = (unit: RowUnitLike) => {
  const pos = new Vector3()
  if (!unit.matrix) return pos
  unit.matrix.decompose(pos, new Quaternion(), new Vector3())
  return pos
}

const getUnitXAxis = (unit: RowUnitLike) => {
  const q = new Quaternion()
  if (!unit.matrix) return new Vector3(1, 0, 0)
  unit.matrix.decompose(new Vector3(), q, new Vector3())
  return new Vector3(1, 0, 0).applyQuaternion(q)
}

export type RowEndUnits = { leftEnd: RowUnitLike; rightEnd: RowUnitLike }

/**
 * Coloca un módulo recto (160/RELAX) en el extremo libre de la fila.
 * Misma rotación que el extremo; nunca “dentro” de una unidad ya colisionada.
 */
export function composeStraightModuleAtRowExterior(
  items: Map<string, any>,
  referenceUnit: RowUnitLike,
  drawWidth: number,
  preferredSide: 'left' | 'right',
): { matrix: Matrix4; endUnitId: string; side: 'left' | 'right' } | null {
  if (!referenceUnit?.matrix || !(drawWidth > 0)) return null
  const ctx = createArmRowContext(items as Map<AnyID, any>, referenceUnit, { getArmSideSign })
  const { leftEnd, rightEnd } = getRowEndUnits(ctx.row, referenceUnit)

  const trySide = (side: 'left' | 'right') => {
    const end = side === 'left' ? leftEnd : rightEnd
    const endMatrix = toMatrix4(end.matrix)
    if (!endMatrix) return null
    const endPos = new Vector3()
    const endQuat = new Quaternion()
    endMatrix.decompose(endPos, endQuat, new Vector3())
    const endX = new Vector3(1, 0, 0).applyQuaternion(endQuat)
    const endWidth = rinconeroPlanWidth(end) || Number(end.width || 0)
    // left → origen a la izquierda del extremo; right → pegado a su cara derecha
    const offset = side === 'left' ? -drawWidth : endWidth
    const pos = endPos.clone().add(endX.multiplyScalar(offset))
    const matrix = new Matrix4().compose(pos, endQuat, new Vector3(1, 1, 1))
    return {
      matrix,
      endUnitId: String(end.id || ''),
      side,
    }
  }

  const preferred = trySide(preferredSide)
  if (preferred) return preferred
  return trySide(preferredSide === 'left' ? 'right' : 'left')
}

/** Extremos izquierdo/derecho de una fila contigua (proyección en eje X del ancla). */
export function getRowEndUnits(row: RowUnitLike[], referenceUnit: RowUnitLike): RowEndUnits {
  const refPos = getWorldPos(referenceUnit)
  const refX = getUnitXAxis(referenceUnit)

  let leftEnd = referenceUnit
  let rightEnd = referenceUnit
  let minEdge = Number.POSITIVE_INFINITY
  let maxEdge = Number.NEGATIVE_INFINITY

  for (const unit of row) {
    const half = rinconeroPlanWidth(unit) / 2 || Number(unit.width || 0) / 2
    const center = getWorldPos(unit).clone().sub(refPos).dot(refX)
    const leftEdge = center - half
    const rightEdge = center + half
    if (leftEdge < minEdge) {
      minEdge = leftEdge
      leftEnd = unit
    }
    if (rightEdge > maxEdge) {
      maxEdge = rightEdge
      rightEnd = unit
    }
  }

  return { leftEnd, rightEnd }
}

export type RowSnapOccupancy = {
  occupiedLeft: string | null
  occupiedRight: string | null
}

/** Lados laterales ocupados respecto al ancla (±80 mm de tolerancia). */
export function inspectRowSideOccupancy(
  itemId: AnyID,
  anchor: RowUnitLike,
  items: Map<string, RowUnitLike & { visible?: boolean; type?: string }>,
): RowSnapOccupancy {
  const result: RowSnapOccupancy = { occupiedLeft: null, occupiedRight: null }
  if (!anchor.matrix) return result

  const anchorPos = getWorldPos(anchor)
  const anchorQuat = new Quaternion()
  anchor.matrix.decompose(new Vector3(), anchorQuat, new Vector3())
  const anchorX = new Vector3(1, 0, 0).applyQuaternion(anchorQuat)
  const anchorZ = new Vector3(0, 0, 1).applyQuaternion(anchorQuat)
  const anchorWidth = rinconeroPlanWidth(anchor) || Number(anchor.width || 0)

  const SIDE_TOLERANCE_MM = 80
  const DEPTH_TOLERANCE_MM = 120
  const HEIGHT_TOLERANCE_MM = 120

  items.forEach((candidate, candidateId) => {
    const cid = String(candidateId)
    if (cid === String(itemId) || cid === String(anchor.id)) return
    if (candidate?.type !== 'linkedItem' || candidate.visible === false || !candidate.matrix) {
      return
    }
    if (/^BR\d+(-\d+)?$/i.test(String(candidate.catalogID || ''))) return

    const cPos = getWorldPos(candidate)
    const delta = cPos.clone().sub(anchorPos)
    const sideProjection = anchorX.dot(new Vector3(delta.x, 0, delta.z))
    const depthProjection = Math.abs(anchorZ.dot(new Vector3(delta.x, 0, delta.z)))
    if (depthProjection > DEPTH_TOLERANCE_MM || Math.abs(delta.y) > HEIGHT_TOLERANCE_MM) return

    const candidateWidth = rinconeroPlanWidth(candidate) || Number(candidate.width || 0)
    const expectedRight = anchorWidth
    const expectedLeft = -candidateWidth

    if (Math.abs(sideProjection - expectedRight) <= SIDE_TOLERANCE_MM) {
      result.occupiedRight = cid
    }
    if (Math.abs(sideProjection - expectedLeft) <= SIDE_TOLERANCE_MM) {
      result.occupiedLeft = cid
    }
  })

  return result
}

/** Lado permitido en finales oreja (177–180): solo un adyacente en el lado habilitado. */
export function allowedOrejaSnapSide(catalogID: unknown): RowSide | null {
  if (!isOreja(catalogID)) return null
  const base = rinconeroBaseCatalogCode(catalogID)
  if (base.endsWith('d')) return -1
  if (base.endsWith('i')) return 1
  return null
}

export type StraightSnapResolution = {
  directionMultiplier: number
  snapSide: 'left' | 'right'
  blocked: boolean
  useLegSnap: boolean
  legArmSide: RowSide | null
}

/**
 * Resuelve dirección de snap en fila recta considerando brazos, orejas y ocupación.
 * Si el acercamiento es perpendicular a un extremo con brazo, activa snap en L (leg).
 */
export function resolveStraightRowSnapDirection(
  itemId: AnyID,
  draggedPos: Vector3,
  anchor: RowUnitLike,
  items: Map<string, RowUnitLike & { visible?: boolean; type?: string }>,
  initialDirection: number,
): StraightSnapResolution {
  const snapSide: 'left' | 'right' = initialDirection < 0 ? 'left' : 'right'
  const rowSide: RowSide = initialDirection < 0 ? -1 : 1

  if (!anchor.matrix) {
    return {
      directionMultiplier: initialDirection,
      snapSide,
      blocked: false,
      useLegSnap: false,
      legArmSide: null,
    }
  }

  const anchorPos = getWorldPos(anchor)
  const anchorQuat = new Quaternion()
  anchor.matrix.decompose(new Vector3(), anchorQuat, new Vector3())
  const anchorX = new Vector3(1, 0, 0).applyQuaternion(anchorQuat)
  const anchorZ = new Vector3(0, 0, 1).applyQuaternion(anchorQuat)
  const approachDelta = draggedPos.clone().sub(anchorPos).setY(0)
  const xApproach = anchorX.dot(approachDelta)
  const zApproach = anchorZ.dot(approachDelta)

  const ctx = createArmRowContext(items as Map<AnyID, RowUnitLike>, anchor, {
    excludeArmId: String(itemId),
    getArmSideSign,
  })

  const orejaSide = allowedOrejaSnapSide(anchor.catalogID)
  const occupancy = inspectRowSideOccupancy(itemId, anchor, items)

  let direction = initialDirection
  let side: 'left' | 'right' = snapSide

  if (orejaSide != null && rowSide !== orejaSide) {
    return {
      directionMultiplier: direction,
      snapSide: side,
      blocked: true,
      useLegSnap: false,
      legArmSide: null,
    }
  }

  if (orejaSide != null) {
    const occupant = orejaSide === -1 ? occupancy.occupiedLeft : occupancy.occupiedRight
    if (occupant && occupant !== String(itemId)) {
      return {
        directionMultiplier: direction,
        snapSide: side,
        blocked: true,
        useLegSnap: false,
        legArmSide: null,
      }
    }
  }

  const armOnApproachSide = ctx.hasArmOnSide(anchor, rowSide)
  const perpendicularDominant = Math.abs(zApproach) > Math.abs(xApproach) * 0.55

  if (armOnApproachSide && perpendicularDominant) {
    return {
      directionMultiplier: direction,
      snapSide: side,
      blocked: false,
      useLegSnap: true,
      legArmSide: rowSide,
    }
  }

  if (armOnApproachSide) {
    const opposite: RowSide = rowSide === 1 ? -1 : 1
    if (orejaSide != null) {
      return {
        directionMultiplier: direction,
        snapSide: side,
        blocked: true,
        useLegSnap: false,
        legArmSide: null,
      }
    }
    if (!ctx.hasArmOnSide(anchor, opposite) && !ctx.isNeighborOnSide(anchor, opposite)) {
      direction = -direction
      side = direction < 0 ? 'left' : 'right'
    } else {
      return {
        directionMultiplier: direction,
        snapSide: side,
        blocked: true,
        useLegSnap: false,
        legArmSide: null,
      }
    }
  }

  const sideOccupant = side === 'left' ? occupancy.occupiedLeft : occupancy.occupiedRight
  if (sideOccupant && sideOccupant !== String(itemId)) {
    if (orejaSide != null) {
      return {
        directionMultiplier: direction,
        snapSide: side,
        blocked: true,
        useLegSnap: false,
        legArmSide: null,
      }
    }
    const otherSide: 'left' | 'right' = side === 'left' ? 'right' : 'left'
    const otherOccupant =
      otherSide === 'left' ? occupancy.occupiedLeft : occupancy.occupiedRight
    const otherRowSide: RowSide = otherSide === 'left' ? -1 : 1
    if (
      otherOccupant &&
      otherOccupant !== String(itemId) &&
      ctx.hasArmOnSide(anchor, otherRowSide)
    ) {
      return {
        directionMultiplier: direction,
        snapSide: side,
        blocked: true,
        useLegSnap: false,
        legArmSide: null,
      }
    }
    if (!otherOccupant || otherOccupant === String(itemId)) {
      if (!ctx.hasArmOnSide(anchor, otherRowSide)) {
        direction = -direction
        side = otherSide
      }
    }
  }

  return {
    directionMultiplier: direction,
    snapSide: side,
    blocked: false,
    useLegSnap: false,
    legArmSide: null,
  }
}

/**
 * Módulo perpendicular en extremo con brazo (comportamiento tipo rinconero / planificador cocina).
 */
export function composeMatrixModuleOnRowEndLeg(
  unit: RinconeroUnitLike,
  moduleWidth: number,
  armSide: RowSide,
): Matrix4 | null {
  if (!unit.matrix) return null

  const position = new Vector3()
  const quaternion = new Quaternion()
  const scale = new Vector3()
  unit.matrix.decompose(position, quaternion, scale)

  const unitWidth = rinconeroPlanWidth(unit) || Number(unit.width || 0)
  const unitFondo = rinconeroPlanFondo(unit)
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(quaternion)

  const rotationAdjustment = new Quaternion().setFromAxisAngle(
    new Vector3(0, 1, 0),
    armSide === -1 ? Math.PI / 2 : -Math.PI / 2,
  )
  const newQuaternion = quaternion.clone().multiply(rotationAdjustment)

  const cornerPos =
    armSide === -1
      ? position.clone()
      : position.clone().add(xAxis.multiplyScalar(unitWidth))

  const zSign = armSide === -1 ? 1 : 1
  const newPosition = cornerPos.clone().add(zAxis.multiplyScalar(zSign * unitFondo))
  alignLegSnapDepthToAnchorBackPlane(newPosition, unit)

  return new Matrix4().compose(newPosition, newQuaternion, scale)
}

export type ChainedDrawPlan = {
  matrix: Matrix4
  anchorUnitId: AnyID
  offset: number
}

/**
 * Coloca el siguiente módulo en el labio exterior libre de brazos (prioridad planificador).
 * Rinconeros y cadenas L delegan en getRelativeMatrix.
 */
export function resolveChainedDrawPlan(
  items: Map<string, RowUnitLike>,
  anchorUnit: RowUnitLike,
  drawCatalogID: unknown,
  drawWidth: number,
  referenceItem?: LinkedItem,
  newItemTipoNormalizado?: string,
): ChainedDrawPlan | null {
  if (!anchorUnit?.matrix) return null

  const anchorMatrix = toMatrix4(anchorUnit.matrix)
  if (!anchorMatrix) return null

  const refIsRinconero = isRinconeroCatalog(anchorUnit.catalogID)
  const drawingIsRinconero = isModularCornerSnapCatalog(drawCatalogID)
  const drawingIsCornerXL = isCornerXLBrazoCatalog(drawCatalogID)

  // 166- y cadenas L: ancla directa. Corner XL brazo usa extremos de fila más abajo.
  if ((refIsRinconero || drawingIsRinconero) && !drawingIsCornerXL) {
    const matrix = getRelativeMatrix(
      anchorMatrix,
      0,
      referenceItem,
      undefined,
      newItemTipoNormalizado,
      drawCatalogID,
    )
    return {
      matrix,
      anchorUnitId: anchorUnit.id as AnyID,
      offset: 0,
    }
  }

  const ctx = createArmRowContext(items as Map<AnyID, RowUnitLike>, anchorUnit, {
    getArmSideSign,
  })
  const { leftEnd, rightEnd } = getRowEndUnits(ctx.row, anchorUnit)

  const leftBlocked = ctx.hasArmOnSide(leftEnd, -1)
  const rightBlocked = ctx.hasArmOnSide(rightEnd, 1)

  // Misma regla que collideItems: derecha = +ancho del ancla; izquierda = −ancho del nuevo.
  // Usar drawWidth en ambos lados solapa Relax (950) ↔ 160 (1420).
  let targetUnit = anchorUnit
  let placeOnRight = true

  if (!rightBlocked && leftBlocked) {
    targetUnit = rightEnd
    placeOnRight = true
  } else if (rightBlocked && !leftBlocked) {
    targetUnit = leftEnd
    placeOnRight = false
  } else if (!rightBlocked && !leftBlocked) {
    const anchorIsLeft = String(leftEnd.id) === String(anchorUnit.id)
    const anchorIsRight = String(rightEnd.id) === String(anchorUnit.id)
    if (anchorIsRight) {
      targetUnit = rightEnd
      placeOnRight = true
    } else if (anchorIsLeft) {
      targetUnit = leftEnd
      placeOnRight = false
    } else {
      targetUnit = rightEnd
      placeOnRight = true
    }
  }

  const targetWidth =
    rinconeroPlanWidth(targetUnit) || Number(targetUnit.width || 0) || drawWidth
  const offset = placeOnRight ? targetWidth : -drawWidth

  const targetMatrix = toMatrix4(targetUnit.matrix)
  if (!targetMatrix) return null

  // referenceItem debe ser el targetUnit (matriz + catálogo coherentes).
  // Si se pasa el ancla original y target es otro extremo, el offset usa mal el quaternion.
  const matrix = getRelativeMatrix(
    targetMatrix,
    offset,
    (targetUnit as LinkedItem) ?? referenceItem,
    undefined,
    newItemTipoNormalizado,
    drawCatalogID,
  )

  return {
    matrix,
    anchorUnitId: targetUnit.id as AnyID,
    offset,
  }
}
